import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
// eslint-disable-next-line @backstage/no-mixed-plugin-imports
import {
  DetailPanel,
  InboxList,
  NotificationErrorBoundary,
  useNotifications,
} from '@mercedes-benz/backstage-plugin-notification-bell';
import { colors, typography, PageLayoutV2 } from '@mercedes-benz/odp-theme-v2';
import { CommunicationsToolbar } from '../CommunicationsToolbar/CommunicationsToolbar';
import type { ActiveFilter, CategoryFilter } from '../types';

export const CommunicationsInbox = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [hasCommsAccess, setHasCommsAccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    discoveryApi.getBaseUrl('comms-manager').then(baseUrl =>
      fetchApi
        .fetch(`${baseUrl}/me`)
        .then(r => (r.ok ? r.json() : null))
        .then(data => {
          if (!cancelled && data?.categories?.length) setHasCommsAccess(true);
        })
        .catch(() => {}),
    );
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi]);

  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const locationStateId =
    (location.state as { notificationId?: string } | null)?.notificationId ??
    null;
  const consumedStateId = useRef<string | null>(null);

  // Read initial filter state from URL
  const initialFilter = (searchParams.get('filter') ?? 'all') as ActiveFilter;
  const initialCategories = (searchParams.get('categories') ?? '')
    .split(',')
    .filter(Boolean) as CategoryFilter[];
  const initialTime = (searchParams.get('time') ?? 'all') as any;
  const initialSearch = searchParams.get('q') ?? '';
  const initialLabels = (searchParams.get('labels') ?? '')
    .split(',')
    .filter(Boolean);

  const {
    error,
    items,
    page,
    setPage,
    pageSize,
    setPageSize,
    pageCount,
    showingRange,
    loading,
    setSelectedCategories,
    selectedLabels,
    setSelectedLabels,
    availableLabels,
    searchQuery,
    setSearchQuery,
    activeTime,
    setActiveTime,
    loadPage,
    toggleNotificationRead,
    deleteNotification,
    markAsRead,
    bulkUpdate,
    setTab,
  } = useNotifications();

  const [activeFilter, setActiveFilter] = useState<ActiveFilter>(initialFilter);
  const [selectedCategoriesLocal, setSelectedCategoriesLocal] =
    useState<CategoryFilter[]>(initialCategories);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [selectedNotification, setSelectedNotification] = useState<
    string | null
  >(null);
  const pendingReadRef = useRef<string | null>(null);

  // Initialise hook state from URL on mount
  useEffect(() => {
    if (initialFilter === 'unread') setTab('unread');
    if (initialCategories.length > 0) setSelectedCategories(initialCategories);
    if (initialTime !== 'all') setActiveTime(initialTime);
    if (initialSearch) setSearchQuery(initialSearch);
    if (initialLabels.length > 0) setSelectedLabels(initialLabels);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync filters to URL whenever they change
  useEffect(() => {
    const params = new URLSearchParams();
    if (activeFilter !== 'all') params.set('filter', activeFilter);
    if (selectedCategoriesLocal.length > 0)
      params.set('categories', selectedCategoriesLocal.join(','));
    if (activeTime !== 'all') params.set('time', activeTime);
    if (searchQuery) params.set('q', searchQuery);
    if (selectedLabels.length > 0)
      params.set('labels', selectedLabels.join(','));
    setSearchParams(params, { replace: true });
  }, [
    activeFilter,
    selectedCategoriesLocal,
    activeTime,
    searchQuery,
    selectedLabels,
    setSearchParams,
  ]);

  const handleCategoryChange = (categories: CategoryFilter[]) => {
    setSelectedCategoriesLocal(categories);
    setSelectedCategories(categories);
    setPage(1);
  };

  const handleFilterChange = (f: ActiveFilter) => {
    setActiveFilter(f);
    if (f === 'unread') setTab('unread');
    else setTab('all');
  };

  const handleCheckToggle = (id: string) =>
    setCheckedIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const handleSelectAll = () => setCheckedIds(new Set(items.map(i => i.id)));
  const handleClearSelection = () => setCheckedIds(new Set());

  const handleBulkMarkRead = async () => {
    await bulkUpdate(Array.from(checkedIds), { read: true });
    setCheckedIds(new Set());
  };
  const handleBulkMarkUnread = async () => {
    await bulkUpdate(Array.from(checkedIds), { read: false });
    setCheckedIds(new Set());
  };
  const handleBulkDismiss = async () => {
    await bulkUpdate(Array.from(checkedIds), { saved: true });
    setCheckedIds(new Set());
  };

  const handleSelectNotification = useCallback(
    (id: string) => {
      setSelectedNotification(id);
      const item = items.find(n => n.id === id);
      if (item && !item.read) markAsRead(id);
    },
    [items, markAsRead],
  );

  // When a notificationId arrives via location state (bell dropdown click),
  // select it immediately and reset to page 1 on the "all" tab so it's visible.
  // Works both on first load and when already on the page (location.state updates on every navigation).
  useEffect(() => {
    if (!locationStateId || locationStateId === consumedStateId.current) return;
    consumedStateId.current = locationStateId;
    pendingReadRef.current = locationStateId;
    setTab('all');
    setPage(1);
    setSelectedNotification(locationStateId);
  }, [locationStateId, setTab, setPage]);

  // After items reload, mark the bell-clicked item as read if needed,
  // then fall back to selecting items[0] when there's no explicit selection.
  useEffect(() => {
    if (items.length === 0) return;
    const pendingId = pendingReadRef.current;
    if (pendingId) {
      const item = items.find(n => n.id === pendingId);
      if (item) {
        pendingReadRef.current = null;
        if (!item.read) markAsRead(pendingId);
      }
      return;
    }
    const isStale =
      selectedNotification && !items.some(n => n.id === selectedNotification);
    if ((!selectedNotification || isStale) && !locationStateId) {
      handleSelectNotification(items[0].id);
    }
  }, [
    items,
    selectedNotification,
    locationStateId,
    markAsRead,
    handleSelectNotification,
  ]);

  const selectedItem = items.find(n => n.id === selectedNotification);

  return (
    <PageLayoutV2
      title="Communications"
      subtitle="Everything that keeps you connected, all in one place."
      headerActions={
        hasCommsAccess ? (
          <Button
            component={Link}
            to="/home/communications/manager"
            variant="contained"
            sx={{
              background: colors.primary.main,
              borderRadius: 20,
              color: '#FFFFFF',
              textTransform: 'none',
              padding: '6px 16px',
              fontFamily: typography.fontFamilyCorpoSText,
              '&:hover': { background: colors.primary.hover },
            }}
          >
            Manage Communications
          </Button>
        ) : undefined
      }
    >
      <NotificationErrorBoundary>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <CommunicationsToolbar
          searchQuery={searchQuery}
          onSearchChange={q => {
            setSearchQuery(q);
            setPage(1);
          }}
          activeFilter={activeFilter}
          onFilterChange={handleFilterChange}
          activeTime={activeTime}
          onTimeChange={t => {
            setActiveTime(t);
            setPage(1);
          }}
          availableLabels={availableLabels}
          selectedLabels={selectedLabels}
          onLabelsChange={setSelectedLabels}
          selectedCategories={selectedCategoriesLocal}
          onCategoryChange={handleCategoryChange}
          data-testid="communications-toolbar"
        />

        <Grid
          container
          spacing={2}
          sx={{
            mt: 1,
            height: 'calc(100vh - 300px)',
            minHeight: 500,
            overflow: 'hidden',
            flexShrink: 0,
            alignItems: 'stretch',
          }}
        >
          {(items.length > 0 || loading) && (
            <Grid
              item
              xs={12}
              md={5}
              sx={{ display: 'flex', flexDirection: 'column' }}
            >
              <Paper
                sx={{
                  flex: '1 1 0',
                  height: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <InboxList
                  items={items}
                  loading={loading}
                  selectedId={selectedNotification}
                  onSelect={handleSelectNotification}
                  searchQuery={searchQuery}
                  categoryFilter={selectedCategoriesLocal[0]}
                  page={page}
                  pageSize={pageSize}
                  pageCount={pageCount}
                  showingRange={showingRange}
                  onPageChange={(newPage: number) => {
                    setPage(newPage);
                    loadPage(newPage);
                  }}
                  onPageSizeChange={(newSize: number) => {
                    setPageSize(newSize);
                    setPage(1);
                  }}
                  checkedIds={checkedIds}
                  onCheckToggle={handleCheckToggle}
                  onSelectAll={handleSelectAll}
                  onClearSelection={handleClearSelection}
                  onBulkMarkRead={handleBulkMarkRead}
                  onBulkMarkUnread={handleBulkMarkUnread}
                  onBulkDismiss={handleBulkDismiss}
                  hasActiveFilters={
                    activeFilter === 'unread' ||
                    activeTime !== 'all' ||
                    selectedCategoriesLocal.length > 0 ||
                    selectedLabels.length > 0 ||
                    !!searchQuery
                  }
                  onClearFilters={() => {
                    handleFilterChange('all');
                    setActiveTime('all');
                    handleCategoryChange([]);
                    setSelectedLabels([]);
                    setSearchQuery('');
                    setPage(1);
                  }}
                />
              </Paper>
            </Grid>
          )}

          <Grid
            item
            xs={12}
            md={items.length > 0 || loading ? 7 : 12}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignSelf: 'stretch',
              maxHeight: '100%',
            }}
          >
            <Paper
              sx={{
                flex: '1 1 0',
                height: 0,
                minHeight: 0,
                p: 3,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <DetailPanel
                selectedItem={selectedItem}
                checkedCount={checkedIds.size}
                onMarkAsUnread={() =>
                  selectedItem &&
                  toggleNotificationRead(
                    selectedItem.id,
                    selectedItem.read ?? false,
                  )
                }
                onDelete={async () => {
                  if (!selectedItem) return;
                  await deleteNotification(selectedItem.id);
                }}
                onLabelClick={label =>
                  setSelectedLabels(
                    selectedLabels.includes(label)
                      ? selectedLabels.filter(l => l !== label)
                      : [...selectedLabels, label],
                  )
                }
                hasActiveFilters={
                  activeFilter === 'unread' ||
                  activeTime !== 'all' ||
                  selectedCategoriesLocal.length > 0 ||
                  selectedLabels.length > 0 ||
                  !!searchQuery
                }
                onClearFilters={() => {
                  handleFilterChange('all');
                  setActiveTime('all');
                  handleCategoryChange([]);
                  setSelectedLabels([]);
                  setSearchQuery('');
                  setPage(1);
                }}
              />
            </Paper>
          </Grid>
        </Grid>
      </NotificationErrorBoundary>
    </PageLayoutV2>
  );
};
